import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { memo } from 'react';
import { Button } from '@/shared/components';
import { cn } from '@/shared/utils';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import {
  Carousel,
  type CarouselApi,
  useCarousel,
} from '@/shared/components/ui/carousel';
import { InternalAccountCarousel } from './InternalAccountCarousel';
import type {
  AccountOption,
  AssetOption,
  InternalAccountSectionCardType,
  InternalTransferFormValues,
  SectionType,
} from '../model';
import type { AccountType } from '@/entities/account';
import type { UseFormReturn } from 'react-hook-form';
import type { TransferInternalSettingsDictionary } from '@/shared/types';
import { useAssetsIcons } from '@/shared/api';

interface InternalAccountSelectorSectionProps {
  title: string;
  totalSlides: number;
  currentSlide: number;
  accounts: InternalAccountSectionCardType[];
  precision?: number;
  selectedAccountTypeFrom?: AccountType | null;
  selectedAccountTypeTo?: AccountType | null;
  selectedAssetFrom?: AssetOption | undefined;
  sectionType: SectionType;
  onSlideChange?: (index: number) => void;
  transferInternalSettings?: TransferInternalSettingsDictionary;
  form: UseFormReturn<InternalTransferFormValues>;
  setSelectedAssetFrom?: Dispatch<SetStateAction<AssetOption | undefined>>;
  setSelectedAssetTo?: Dispatch<SetStateAction<AssetOption | undefined>>;
  setSelectedAccountFrom?: Dispatch<SetStateAction<AccountOption | undefined>>;
  setSelectedAccountTo?: Dispatch<SetStateAction<AccountOption | undefined>>;
  accountOptions?: AccountOption[];
}

function CarouselControls() {
  const { scrollPrev, scrollNext, canScrollPrev, canScrollNext } =
    useCarousel();

  return (
    <div className="flex items-center gap-[1.6rem]">
      <Button
        type="button"
        variant="outline"
        disabled={!canScrollPrev}
        onClick={scrollPrev}
        className="size-[4.2rem] rounded-full bg-background border border-border shadow-xs flex items-center justify-center p-0 hover:bg-accent disabled:opacity-50">
        <ChevronLeftIcon className="w-[2.4rem] h-[2.4rem]" />
      </Button>
      <Button
        type="button"
        variant="outline"
        disabled={!canScrollNext}
        onClick={scrollNext}
        className="size-[4.2rem] rounded-full bg-background border border-border shadow-xs flex items-center justify-center p-0 hover:bg-accent disabled:opacity-50">
        <ChevronRightIcon className="w-[2.4rem] h-[2.4rem]" />
      </Button>
    </div>
  );
}

function InternalTransferCarouselHeader({ title }: { title: string }) {
  return (
    <div className="flex items-center justify-between gap-[1.6rem]">
      <div className="headline4 min-h-6 flex-none flex-grow text-foreground">
        {title}
      </div>
      <CarouselControls />
    </div>
  );
}

function InternalTransferCarouselIndicators({
  totalSlides,
  currentSlide,
}: {
  totalSlides: number;
  currentSlide: number;
}) {
  const indicators = useMemo(() => {
    return Array.from({ length: totalSlides }, (_, index) => {
      const isActive = index === currentSlide;
      return {
        index,
        isActive,
      };
    });
  }, [totalSlides, currentSlide]);

  if (totalSlides === 0) {
    return null;
  }

  return (
    <div className="flex flex-row items-center justify-center p-0 gap-[4px] w-full h-4 flex-none flex-grow-0">
      {indicators.map(({ index, isActive }) => (
        <div
          key={`indicator-${index}`}
          className={cn(
            'rounded-[3px] flex-none flex-grow-0 transition-all duration-200',
            isActive
              ? 'w-[10px] h-[4px] bg-foreground'
              : 'w-[4px] h-[4px] bg-[#BFC1C5] dark:bg-[#5F5F5F]'
          )}
        />
      ))}
    </div>
  );
}

export const InternalAccountSelectorSection = memo(
  function InternalAccountSelectorSection({
    title,
    totalSlides,
    currentSlide,
    accounts,
    selectedAccountTypeFrom,
    selectedAccountTypeTo,
    selectedAssetFrom,
    sectionType,
    onSlideChange,
    transferInternalSettings,
    form,
    setSelectedAssetFrom,
    setSelectedAssetTo,
    setSelectedAccountFrom,
    setSelectedAccountTo,
    accountOptions,
  }: InternalAccountSelectorSectionProps) {
    const { assetsIconsData } = useAssetsIcons();

    const [api, setApi] = useState<CarouselApi | undefined>(undefined);
    const isInternalChangeRef = useRef(false);

    useEffect(() => {
      if (!api || !onSlideChange) return;

      const updateSlide = () => {
        if (isInternalChangeRef.current) {
          isInternalChangeRef.current = false;
          return;
        }
        const selectedIndex = api.selectedScrollSnap();
        onSlideChange(selectedIndex);
      };

      api.on('select', updateSlide);
      updateSlide();

      return () => {
        api.off('select', updateSlide);
      };
    }, [api, onSlideChange]);

    useEffect(() => {
      if (!api) return;
      const currentIndex = api.selectedScrollSnap();
      if (currentIndex !== currentSlide) {
        isInternalChangeRef.current = true;
        api.scrollTo(currentSlide);
      }
    }, [api, currentSlide]);

    return (
      <div className="flex flex-col gap-[1.6rem] w-full">
        <Carousel
          opts={{
            align: 'start',
          }}
          setApi={setApi}
          className="w-full flex flex-col gap-[0.8rem]">
          <InternalTransferCarouselHeader title={title} />

          <InternalAccountCarousel
            accounts={accounts}
            assetsIconsData={assetsIconsData?.value}
            selectedAccountTypeFrom={selectedAccountTypeFrom}
            selectedAccountTypeTo={selectedAccountTypeTo}
            selectedAssetFrom={selectedAssetFrom}
            sectionType={sectionType}
            transferInternalSettings={transferInternalSettings}
            form={form}
            setSelectedAssetFrom={setSelectedAssetFrom}
            setSelectedAssetTo={setSelectedAssetTo}
            setSelectedAccountFrom={setSelectedAccountFrom}
            setSelectedAccountTo={setSelectedAccountTo}
            accountOptions={accountOptions}
          />

          <InternalTransferCarouselIndicators
            totalSlides={totalSlides}
            currentSlide={currentSlide}
          />
        </Carousel>
      </div>
    );
  }
);
